import React from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskPill, DeskRowMenu, DeskTable } from "../../desktop/DeskTable";
import { formatEstimateStatus } from "../../../helpers/estimates";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { estimateStatusTone } from "./estimateDesktopUi";
import "./estimateDesktop.css";

export function DeskLine({ label, value, emphasis = false, indent = false }) {
  return (
    <div className={`est-d-line${emphasis ? " is-emph" : ""}${indent ? " is-indent" : ""}`}>
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

const LOCATION_COLUMNS = [
  { key: "item", label: "Feature", width: "46%", render: (row) => <span className="dt-strong">{row.label}</span> },
  { key: "materials", label: "Materials", align: "right", width: "18%", render: (row) => <span className="dt-num">{formatCurrency(row.materialsTotal)}</span> },
  { key: "labour", label: "Labour", align: "right", width: "18%", render: (row) => <span className="dt-num">{formatCurrency(row.labourTotal)}</span> },
  { key: "total", label: "Total", align: "right", width: "18%", render: (row) => <span className="dt-num">{formatCurrency(row.workTypeTotal)}</span> },
];

/**
 * Desktop Estimate Summary (pricing & status). Presentational: all state and
 * handlers stay in EstimateSummaryScreen. Main column = totals, location
 * breakdown table, adjustments / final price / tax inputs; side column =
 * identity, status, grand total and Save Pricing.
 */
export function EstimateSummaryDesktop({
  title,
  clientName,
  estimateNumber,
  status,
  updatedLabel,
  onBack,
  statusControl,
  actions,
  archiveItem,
  onArchive,
  financials,
  taxSummary,
  liveAdjustedTotal,
  visibleLocations,
  pricingSchemaMissing,
  adjustmentsNode,
  finalPriceNode,
  taxNode,
  canSavePricing,
  saving,
  onSavePricing,
  linkedProject,
  children,
}) {
  const groups = visibleLocations.map((location) => ({
    key: location.locationId,
    header: (
      <div className="dt-group__row">
        <span>{location.locationName}</span>
        <span className="dt-group__total">{formatCurrency(location.locationTotal)}</span>
      </div>
    ),
    rows: location.workTypes,
  }));

  return (
    <CrewPilotModuleShell
      title={title}
      subtitle={clientName}
      onBack={onBack}
      desktopBackLabel="Estimates"
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          {actions}
          {archiveItem ? (
            <DeskRowMenu label="Estimate actions" items={[archiveItem]} onSelect={onArchive} />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Estimate totals"
        className="est-d-kpi4"
        tiles={[
          { key: "mat", value: formatCurrency(financials.calculatedMaterialsTotal), label: "Materials", tone: "blue" },
          { key: "lab", value: formatCurrency(financials.calculatedLabourTotal), label: "Labour", tone: "muted" },
          { key: "calc", value: formatCurrency(financials.calculatedEstimateTotal), label: "Calculated Total", tone: "gold" },
          { key: "grand", value: formatCurrency(taxSummary.grandTotal), label: "Grand Total", tone: "green" },
        ]}
      />

      <div className="dp-grid dp-grid--main-side">
        <div className="est-d-stack">
          <DeskPanel title="Location Summary" className="dp--flush">
            {visibleLocations.length === 0 ? (
              <DeskEmpty title="No locations yet">Add locations from Edit Estimate.</DeskEmpty>
            ) : (
              <DeskTable
                testId="estimate-location-summary"
                className="est-d-flat"
                caption="Location summary"
                columns={LOCATION_COLUMNS}
                groups={groups}
                rowKey={(row) => row.workTypeId}
              />
            )}
          </DeskPanel>

          <DeskPanel title="Project Adjustments">
            {pricingSchemaMissing ? (
              <div className="est-d-warn">
                Pricing fields are not applied yet. Run migrations{" "}
                <code>20260625120000_estimate_pricing_summary.sql</code> and{" "}
                <code>20260629120000_estimate_tax_summary.sql</code> in Supabase.
              </div>
            ) : null}
            {adjustmentsNode}
            <hr className="est-d-hr" />
            <p className="est-d-subhead">Final Price</p>
            {finalPriceNode}
          </DeskPanel>

          <DeskPanel title="Tax">
            {taxNode}
          </DeskPanel>
        </div>

        <aside className="est-d-side" aria-label="Estimate details">
          <DeskPanel title="Estimate">
            <div className="est-d-id">
              <span>{estimateNumber}</span>
              <DeskPill tone={estimateStatusTone(status)}>{formatEstimateStatus(status)}</DeskPill>
            </div>
            <dl className="dp-kv dp-kv--tight">
              <dt>Client</dt>
              <dd>{clientName}</dd>
              <dt>Updated</dt>
              <dd>{updatedLabel}</dd>
              {linkedProject ? (
                <>
                  <dt>Project</dt>
                  <dd>{linkedProject}</dd>
                </>
              ) : null}
            </dl>
            {statusControl ? <div style={{ marginTop: 14 }}>{statusControl}</div> : null}
          </DeskPanel>

          <DeskPanel title="Pricing">
            <DeskLine label="Calculated Estimate Total" value={financials.calculatedEstimateTotal} />
            <DeskLine label="Adjusted Total" value={liveAdjustedTotal} />
            {financials.finalEstimatePrice != null ? (
              <DeskLine label="Saved Final Price" value={financials.finalEstimatePrice} />
            ) : null}
            <hr className="est-d-hr" />
            <DeskLine label="Subtotal" value={taxSummary.subtotal} />
            {taxSummary.lines.map((line) => (
              <DeskLine
                key={`${line.type}-${line.label}-${line.rate}`}
                label={`${line.label} (${line.rate}%)`}
                value={line.amount}
                indent
              />
            ))}
            <DeskLine label="Grand Total" value={taxSummary.grandTotal} emphasis />
            {canSavePricing ? (
              <div className="dp-actions">
                <button type="button" className="dt-btn dt-btn--primary est-d-btn-block" onClick={onSavePricing} disabled={saving}>
                  {saving ? "Saving…" : "Save Pricing"}
                </button>
              </div>
            ) : null}
          </DeskPanel>
        </aside>
      </div>
      {children}
    </CrewPilotModuleShell>
  );
}
